if (!customElements.get('responsive-slideshow-media')) {
  class ResponsiveSlideshowMedia extends HTMLElement {
    static breakpoint = window.matchMedia('(max-width: 749px)');
    static instances = new Set();

    static handleBreakpointChange() {
      ResponsiveSlideshowMedia.playActiveInstances(true);
    }

    static playActiveInstances(pauseAllInstances = false) {
      const instances = Array.from(ResponsiveSlideshowMedia.instances);
      const activeInstances = instances.filter((instance) => instance.isActiveAutoplay());
      const instancesToPause = pauseAllInstances ? instances : activeInstances;

      instancesToPause.forEach((instance) => instance.pauseResponsiveMedia());
      activeInstances.forEach((instance) => instance.loadSelectedMedia());
      activeInstances.forEach((instance) => instance.playSelectedMedia());
    }

    constructor() {
      super();
      this.breakpoint = ResponsiveSlideshowMedia.breakpoint;
      this.playActiveMedia = this.playActiveMedia.bind(this);
    }

    connectedCallback() {
      this.slide = this.closest('.slideshow__slide');
      this.slideshow = this.closest('slideshow-component');
      this.desktopMedia = this.querySelector('[data-media-variant="desktop"]');
      this.mobileMedia = this.querySelector('[data-media-variant="mobile"]');

      if (!this.slide || !this.desktopMedia) return;

      this.slideObserver = new MutationObserver(this.playActiveMedia);
      this.slideObserver.observe(this.slide, { attributes: true, attributeFilter: ['aria-hidden'] });
      ResponsiveSlideshowMedia.instances.add(this);
      if (ResponsiveSlideshowMedia.instances.size === 1) {
        this.breakpoint.addEventListener('change', ResponsiveSlideshowMedia.handleBreakpointChange);
      }
      this.playActiveMedia();
    }

    disconnectedCallback() {
      this.slideObserver?.disconnect();
      ResponsiveSlideshowMedia.instances.delete(this);
      if (ResponsiveSlideshowMedia.instances.size === 0) {
        this.breakpoint.removeEventListener('change', ResponsiveSlideshowMedia.handleBreakpointChange);
      }
    }

    slideIsActive() {
      const ariaHidden = this.slide.getAttribute('aria-hidden');
      if (ariaHidden !== null) return ariaHidden === 'false';
      return this.slide === this.slideshow?.querySelector('.slideshow__slide');
    }

    isActiveAutoplay() {
      return this.dataset.autoplay === 'true' && this.slideIsActive();
    }

    selectedMedia() {
      return this.breakpoint.matches && this.mobileMedia ? this.mobileMedia : this.desktopMedia;
    }

    pauseResponsiveMedia() {
      [this.desktopMedia, this.mobileMedia].forEach((media) => {
        if (!media) return;

        media.querySelector('video')?.pause();
        media
          .querySelector('.js-youtube')
          ?.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        media.querySelector('.js-vimeo')?.contentWindow.postMessage('{"method":"pause"}', '*');
      });
    }

    loadSelectedMedia() {
      const media = this.selectedMedia();
      if (media.hasAttribute('loaded')) return;

      const deferredElement = media.querySelector('template')?.content.firstElementChild?.cloneNode(true);
      if (!deferredElement) return;

      media.setAttribute('loaded', true);
      media.appendChild(deferredElement);
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay') !== null) {
        const playPromise = deferredElement.play();
        if (playPromise !== undefined) playPromise.catch(() => {});
      }

      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => deferredElement.setAttribute('style', formerStyle), 0);
    }

    playSelectedMedia() {
      const media = this.selectedMedia();
      const video = media.querySelector('video');
      if (video) {
        const playPromise = video.play();
        if (playPromise !== undefined) playPromise.catch(() => {});
      }

      media
        .querySelector('.js-youtube')
        ?.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      media.querySelector('.js-vimeo')?.contentWindow.postMessage('{"method":"play"}', '*');
    }

    playActiveMedia() {
      ResponsiveSlideshowMedia.playActiveInstances();
    }
  }

  customElements.define('responsive-slideshow-media', ResponsiveSlideshowMedia);
}
